import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { environment } from "@/environments/environment";
import { backend } from "@/integrations/backend/client";
import { AuthService, Profile, User } from "../core/auth.service";
import { IslandMapComponent } from "../game/island-map.component";

const INDEX_DEBUG_PREFIX = "[LINA][index]";

function logIndexStep(step: string, details?: unknown) {
  if (!environment.production) {
    console.log(`${INDEX_DEBUG_PREFIX} ${step}`, details ?? "");
  }
}

@Component({
  selector: "app-index-page",
  standalone: true,
  imports: [CommonModule, LucideDynamicIcon, IslandMapComponent],
  template: `
    <div
      *ngIf="loading || checkingAssessment"
      class="min-h-screen flex items-center justify-center bg-background ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>
    <app-island-map *ngIf="!loading && !checkingAssessment && user" />
  `,
})
export class IndexPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);

  user: User | null = null;
  profile: Profile | null = null;
  loading = true;
  checkingAssessment = true;
  private checkedAssessmentForUserId: string | null = null;
  private subscription?: Subscription;

  ngOnInit() {
    logIndexStep("ngOnInit");
    this.subscription = this.auth.state$.subscribe((state) => {
      logIndexStep("authState", {
        loading: state.loading,
        hasUser: Boolean(state.user),
        userId: state.user?.id ?? null,
        hasProfile: Boolean(state.profile),
        onboardingCompleted: Boolean(state.profile?.onboarding_completed_at),
        checkingAssessment: this.checkingAssessment,
      });

      this.user = state.user;
      this.profile = state.profile;
      this.loading = state.loading;
      this.changeDetector.detectChanges();

      if (!state.loading && !state.user) {
        logIndexStep("navigate", { reason: "no-user", target: "/auth" });
        void this.router.navigateByUrl("/auth");
        return;
      }

      if (state.user) {
        if (!state.profile) {
          if (!state.loading) {
            this.checkingAssessment = false;
            this.changeDetector.detectChanges();
            logIndexStep("navigate", {
              reason: "missing-profile",
              target: "/auth",
            });
            void this.router.navigateByUrl("/auth");
          }
          return;
        }

        if (!state.profile.onboarding_completed_at) {
          this.checkingAssessment = false;
          this.changeDetector.detectChanges();
          logIndexStep("navigate", {
            reason: "onboarding-pending",
            target: "/tutorial",
          });
          void this.router.navigateByUrl("/tutorial");
          return;
        }

        if (this.checkedAssessmentForUserId === state.user.id) {
          logIndexStep("assessment:alreadyChecked", { userId: state.user.id });
          return;
        }
        this.checkedAssessmentForUserId = state.user.id;
        logIndexStep("assessment:check:start", { userId: state.user.id });
        void this.checkFirstTimeUser();
      }
    });
  }

  ngOnDestroy() {
    logIndexStep("ngOnDestroy");
    this.subscription?.unsubscribe();
  }

  private async checkFirstTimeUser() {
    if (!this.user) {
      this.checkingAssessment = false;
      this.changeDetector.detectChanges();
      return;
    }

    this.checkingAssessment = true;
    this.changeDetector.detectChanges();
    try {
      const { data, error } = await backend
        .from("self_assessment_attempts")
        .select("id")
        .eq("user_id", this.user.id)
        .eq("assessment_type", "initial")
        .limit(1);

      if (error) throw error;

      if (!data || data.length === 0) {
        logIndexStep("assessment:missing", {
          target: "/self-assessment",
        });
        await this.router.navigateByUrl("/self-assessment");
        return;
      }

      logIndexStep("assessment:found", { attempts: data.length });
    } catch (error) {
      console.error("Error checking assessment status:", error);
    } finally {
      logIndexStep("assessment:check:finish");
      this.checkingAssessment = false;
      this.changeDetector.detectChanges();
    }
  }
}
